import * as React from 'react';

import type { ValidationErrors } from '../../components/register-user-form/RegisterUserForm.types';
import type {
  UseRegisterUserFormProps,
  UseRegisterUserFormReturn,
} from './useRegisterUserForm.types';

import {
  isEmailConflictError,
  validateForm,
} from '../../components/register-user-form/RegisterUserForm.utils';
import { useRegisterUser } from '../use-register-user/useRegisterUser';

function useRegisterUserForm({
  onSubmitSuccess,
  onEmailExists,
}: Readonly<UseRegisterUserFormProps>): UseRegisterUserFormReturn {
  const { mutate, isPending, isError, error } = useRegisterUser();

  const [firstName, setFirstName] = React.useState('');
  const [middleName, setMiddleName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [validationErrors, setValidationErrors] =
    React.useState<ValidationErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const handleBlur = React.useCallback(
    (field: string): void => {
      setTouched(prev => ({ ...prev, [field]: true }));
      const errors = validateForm({ firstName, lastName, email });
      setValidationErrors(errors);
    },
    [firstName, lastName, email]
  );

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const errors = validateForm({ firstName, lastName, email });
      setValidationErrors(errors);
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
      });

      if (Object.keys(errors).length > 0) return;

      mutate(
        { email, firstName, lastName, ...(middleName ? { middleName } : {}) },
        {
          onSuccess: () => {
            sessionStorage.setItem('loginHint', email);
            onSubmitSuccess();
          },
          onError: (err: Error) => {
            if (isEmailConflictError(err)) {
              sessionStorage.setItem('loginHint', email);
              onEmailExists();
            }
          },
        }
      );
    },
    [
      firstName,
      middleName,
      lastName,
      email,
      mutate,
      onSubmitSuccess,
      onEmailExists,
    ]
  );

  return {
    firstName,
    middleName,
    lastName,
    email,
    validationErrors,
    touched,
    isPending,
    isError,
    error,
    setFirstName,
    setMiddleName,
    setLastName,
    setEmail,
    handleBlur,
    handleSubmit,
  };
}

export { useRegisterUserForm };
